import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { CheckCircle, Info, ImageOff, AlertTriangle } from 'lucide-react';
import { 
  SAQ_CONFIG, 
  validateSAQAnswers, 
  calculateSAQScore,
  getScoreColor
} from './saqSchema';

/**
 * SAQQuestionnaire - Componente para el cuestionario SAQ (Scoliosis Appearance Questionnaire)
 * 
 * Props:
 * - onComplete: callback con los resultados
 * - initialAnswers: respuestas previas (para edición)
 * - config: configuración personalizada
 * - allowIncomplete: permitir guardar incompleto (para médicos)
 * - showImages: mostrar imágenes si están disponibles
 */
const SAQQuestionnaire = ({ 
  onComplete, 
  initialAnswers = {}, 
  config = SAQ_CONFIG,
  allowIncomplete = false,
  showImages = true
}) => {
  const totalItems = config.totalItems || 14;
  
  const [answers, setAnswers] = useState(() => {
    const initial = {};
    for (let i = 1; i <= totalItems; i++) {
      initial[`item${i}`] = initialAnswers[`item${i}`] ?? null;
    }
    return initial;
  });
  
  const [errors, setErrors] = useState([]);
  const [imageErrors, setImageErrors] = useState({});

  // Calcular progreso
  const answeredCount = Object.values(answers).filter(v => v !== null).length;
  const progress = (answeredCount / totalItems) * 100;

  // Manejar cambio de respuesta
  const handleAnswerChange = (itemNumber, value) => {
    setAnswers(prev => ({
      ...prev,
      [`item${itemNumber}`]: value
    }));
    setErrors([]);
  };

  // Manejar error de imagen
  const handleImageError = (itemNumber) => {
    setImageErrors(prev => ({
      ...prev,
      [itemNumber]: true
    }));
  };

  // Manejar submit
  const handleSubmit = () => {
    const validation = validateSAQAnswers(answers, allowIncomplete, config);
    
    if (!validation.isValid && !allowIncomplete) {
      setErrors(validation.errors);
      return;
    }
    
    setErrors([]);
    
    const scores = calculateSAQScore(answers, config);
    
    onComplete({
      answers,
      ...scores,
      questionnaire_type: 'SAQ',
      version: config.version,
      completed_at: new Date().toISOString(),
      is_draft: !validation.isValid && allowIncomplete
    });
  };

  // Obtener opciones para un ítem
  const getItemOptions = (itemNumber) => {
    const itemConfig = config.texts.items[`item${itemNumber}`];
    return itemConfig?.options || config.texts.defaultOptions;
  };

  // Obtener texto del ítem
  const getItemText = (itemNumber) => {
    const itemConfig = config.texts.items[`item${itemNumber}`];
    return itemConfig?.text || `[Pregunta ${itemNumber}]`;
  };

  // Verificar si el ítem tiene imagen
  const hasItemImage = (itemNumber) => {
    if (!showImages || !config.enableImages) return false;
    const itemConfig = config.texts.items[`item${itemNumber}`];
    return itemConfig?.hasImage && itemConfig?.imageFile && !imageErrors[itemNumber];
  };

  // Obtener URL de la imagen
  const getImageUrl = (itemNumber) => {
    const itemConfig = config.texts.items[`item${itemNumber}`];
    if (!itemConfig?.imageFile) return null;
    return `${config.imageBasePath || '/assets/questionnaires/saq/'}${itemConfig.imageFile}`;
  };

  // Preview de puntuación
  const previewScores = calculateSAQScore(answers, config);

  // Instrucciones según si hay imágenes
  const hasAnyImages = showImages && config.enableImages && 
    Object.values(config.texts.items).some(item => item?.hasImage);
  const instructions = hasAnyImages 
    ? config.texts.instructions 
    : config.texts.instructionsNoImages;

  return (
    <div className="space-y-6">
      {/* Cabecera con instrucciones */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-purple-900">{config.texts.title}</h3>
            <p className="text-sm text-purple-700 mt-1">{instructions}</p>
          </div>
        </div>
      </div>

      {/* Barra de progreso general */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progreso</span>
          <span className="font-medium">{answeredCount} de {totalItems} preguntas</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Preguntas */}
      <div className="space-y-4">
        {Array.from({ length: totalItems }, (_, idx) => {
          const itemNumber = idx + 1;
          const isAnswered = answers[`item${itemNumber}`] !== null;
          const itemText = getItemText(itemNumber);
          const options = getItemOptions(itemNumber);
          const showItemImage = hasItemImage(itemNumber);
          const imageUrl = getImageUrl(itemNumber);

          return (
            <Card 
              key={itemNumber}
              className={`border-2 transition-colors ${
                isAnswered 
                  ? 'border-purple-200 bg-purple-50/30' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isAnswered ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {isAnswered ? <CheckCircle className="w-4 h-4" /> : itemNumber}
                  </span>
                  <span className="text-gray-900">Pregunta {itemNumber}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-700 mb-4 ml-10">{itemText}</p>
                
                {/* Imagen si existe */}
                {showItemImage && imageUrl && (
                  <div className="ml-10 mb-4">
                    <div className="bg-gray-100 rounded-lg p-4 inline-block">
                      <img 
                        src={imageUrl}
                        alt={`Figura ${itemNumber}`}
                        className="max-w-full h-auto max-h-48 object-contain"
                        onError={() => handleImageError(itemNumber)}
                      />
                    </div>
                  </div>
                )}
                
                {/* Mensaje si la imagen no cargó */}
                {showImages && config.enableImages && 
                 config.texts.items[`item${itemNumber}`]?.hasImage && 
                 imageErrors[itemNumber] && (
                  <div className="ml-10 mb-4 flex items-center gap-2 text-gray-500 text-sm">
                    <ImageOff className="w-4 h-4" />
                    <span>Imagen no disponible</span>
                  </div>
                )}
                
                {/* Opciones de respuesta */}
                <div className="ml-10 space-y-2">
                  {options.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        answers[`item${itemNumber}`] === option.value
                          ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`item${itemNumber}`}
                        value={option.value}
                        checked={answers[`item${itemNumber}`] === option.value}
                        onChange={() => handleAnswerChange(itemNumber, option.value)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className={`flex-1 text-sm ${
                        answers[`item${itemNumber}`] === option.value 
                          ? 'font-medium text-purple-900' 
                          : 'text-gray-700'
                      }`}>
                        {option.label}
                      </span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded"
                        style={{ 
                          backgroundColor: `${option.color || '#6B7280'}20`,
                          color: option.color || '#6B7280'
                        }}
                      >
                        {option.value}
                      </span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview de puntuación */}
      {answeredCount > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Puntuación parcial</h4>
            {previewScores.total_score !== null && (
              <div className="text-right">
                <p 
                  className="text-2xl font-bold"
                  style={{ color: getScoreColor(previewScores.total_score, config) }}
                >
                  {config.totalScoreMethod === 'sum' 
                    ? previewScores.total_score 
                    : previewScores.mean_score?.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">
                  {config.totalScoreMethod === 'sum' 
                    ? `de ${totalItems * config.maxValue} puntos` 
                    : 'media de ítems'}
                </p>
              </div>
            )}
          </div>
          
          {previewScores.interpretation && (
            <div className="flex items-center gap-2 mt-2">
              <span 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: getScoreColor(previewScores.total_score, config) }}
              />
              <p className="text-sm text-gray-600">
                <strong>Interpretación:</strong> {previewScores.interpretation}
              </p>
            </div>
          )}

          {/* Barra visual del score */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Sin afectación</span>
              <span>Afectación severa</span>
            </div>
            <div className="h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 rounded-full relative">
              {previewScores.total_score_percentage !== null && (
                <div 
                  className="absolute w-3 h-3 bg-white border-2 border-gray-700 rounded-full -top-0.5"
                  style={{ left: `${Math.min(previewScores.total_score_percentage, 100)}%`, transform: 'translateX(-50%)' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Errores */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium mb-1">Por favor complete las siguientes preguntas:</p>
              <p className="text-sm text-red-600">
                {errors.slice(0, 5).map(e => e.replace('El ítem ', '')).join(', ')}
                {errors.length > 5 && ` y ${errors.length - 5} más`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Botón guardar */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {allowIncomplete && answeredCount > 0 && answeredCount < totalItems && (
          <Button
            variant="outline"
            onClick={handleSubmit}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Guardar borrador ({answeredCount}/{totalItems})
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          className="bg-purple-600 hover:bg-purple-700"
          disabled={answeredCount < totalItems && !allowIncomplete}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Guardar Cuestionario
        </Button>
      </div>
    </div>
  );
};

export default SAQQuestionnaire;
